"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Link from "next/link";

interface Project {
  image: string;
  alt: string;
  titleEn: string;
  titleZh: string;
  category: string;
  area: string;
  duration: string;
}

const PROJECTS: Project[] = [
{
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_119136500-1769683833421.png",
  alt: "Luxury open-plan living room and dining area renovation in Hong Kong residential building",
  titleEn: "Taikoo Shing Residence",
  titleZh: "太古城豪宅翻新",
  category: "Residential",
  area: "1,200 sq ft",
  duration: "5 months"
},
{
  image: "https://images.unsplash.com/photo-1600122122171-a215d11a965e",
  alt: "Modern luxury kitchen renovation with white cabinetry and marble island",
  titleEn: "Kowloon Tong Kitchen",
  titleZh: "九龍塘廚房改造",
  category: "Kitchen",
  area: "180 sq ft",
  duration: "3 weeks"
},
{
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_145d460fd-1765180551539.png",
  alt: "Contemporary master bedroom design with custom built-in wardrobe and accent wall",
  titleEn: "Tsim Sha Tsui Master Suite",
  titleZh: "尖沙咀主人房設計",
  category: "Bedroom",
  area: "320 sq ft",
  duration: "6 weeks"
},
{
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d77a9752-1767196504972.png",
  alt: "Open-plan modern office renovation with collaborative workspace and glass partitions",
  titleEn: "Mong Kok Creative Office",
  titleZh: "旺角創意辦公室",
  category: "Commercial",
  area: "2,500 sq ft",
  duration: "8 weeks"
},
{
  image: "https://images.unsplash.com/photo-1686943812586-65d1d30ab40f",
  alt: "Spa-inspired bathroom renovation with freestanding tub and large format tiles",
  titleEn: "Wan Chai Spa Bathroom",
  titleZh: "灣仔水療浴室",
  category: "Bathroom",
  area: "90 sq ft",
  duration: "3 weeks"
},
{
  image: "https://images.unsplash.com/photo-1697457053997-555dfc117cf1",
  alt: "Modern minimalist living room with custom TV wall and built-in storage",
  titleEn: "Sham Shui Po Living Room",
  titleZh: "深水埗客廳翻新",
  category: "Residential",
  area: "600 sq ft",
  duration: "2 months"
},
{
  image: "https://images.unsplash.com/photo-1722764375892-32aa3e1811da",
  alt: "Elegant dining room with custom lighting and marble dining table",
  titleEn: "Causeway Bay Dining Room",
  titleZh: "銅鑼灣飯廳設計",
  category: "Residential",
  area: "150 sq ft",
  duration: "4 weeks"
},
{
  image: "https://images.unsplash.com/photo-1581299894303-5a293523369b",
  alt: "Luxury hotel-style bedroom renovation with custom headboard and ambient lighting",
  titleEn: "Jordan Hotel Suite",
  titleZh: "佐敦酒店式套房",
  category: "Commercial",
  area: "450 sq ft",
  duration: "5 weeks"
}];


const CATEGORIES = ["All · 全部", "Residential", "Kitchen", "Bathroom", "Bedroom", "Commercial"];

export default function PortfolioSection() {
  const [activeCategory, setActiveCategory] = useState("All · 全部");
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const filtered = activeCategory === "All · 全部" ?
  PROJECTS :
  PROJECTS.filter((p) => p.category === activeCategory);

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll(".port-reveal");
    if (!elements) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove("hidden-reveal");
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => {
      el.classList.add("hidden-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="portfolio"
      ref={sectionRef}
      className="py-24 md:py-32 noise relative"
      style={{ background: "linear-gradient(180deg, #0A1628 0%, #0D1E3A 50%, #0A1628 100%)" }}>
      
      <div className="px-6 md:px-12 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="port-reveal reveal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                Portfolio · 精選項目
              </span>
            </div>
            <h2 className="font-display section-title text-white">
              我們的<br />
              <span className="italic" style={{ color: "#D4AF37" }}>Signature</span> Works
            </h2>
          </div>
          <p className="port-reveal reveal text-base max-w-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.6)" }}>
            每個項目都是我們對品質承諾的見證。瀏覽我們在香港各區完成的精選裝修項目。
          </p>
        </div>

        {/* Category Filter */}
        <div className="port-reveal reveal flex flex-wrap gap-3 mb-10">
          {CATEGORIES.map((cat) =>
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className="px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300"
            style={{
              background: activeCategory === cat ?
              "linear-gradient(135deg, #D4AF37, #A8891E)" :
              "rgba(255,255,255,0.06)",
              color: activeCategory === cat ? "#0A1628" : "rgba(245,240,232,0.6)",
              border: activeCategory === cat ? "none" : "1px solid rgba(212,175,55,0.2)"
            }}>
            
              {cat}
            </button>
          )}
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filtered.map((project, index) =>
        <Link
          key={index}
          href="/projects/taikoo-shing-residence"
          className="group block port-reveal reveal cursor-pointer">
          
            <div className="relative overflow-hidden rounded-2xl h-80 transition-all duration-500 group-hover:scale-[1.02]">
              <AppImage
              src={project.image}
              alt={project.alt}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-110" />
            
              <div
              className="absolute inset-0 transition-opacity duration-500"
              style={{ background: "linear-gradient(to top, rgba(10,22,40,0.95) 0%, rgba(10,22,40,0.3) 60%, transparent 100%)" }} />
            
              {/* Category Badge */}
              <div className="absolute top-4 left-4">
                <div
                className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                style={{ background: "rgba(212,175,55,0.2)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}>
                
                  {project.category}
                </div>
              </div>
              {/* Hover Arrow */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(212,175,55,0.9)" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0A1628" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </div>
              </div>
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-display text-xl font-bold text-white mb-1 group-hover:text-gold-DEFAULT transition-colors">
                  {project.titleEn}
                </h3>
                <p className="text-sm mb-3" style={{ color: "#D4AF37" }}>
                  {project.titleZh}
                </p>
                <div className="flex items-center gap-4 text-xs" style={{ color: "rgba(245,240,232,0.6)" }}>
                  <span>📐 {project.area}</span>
                  <span>⏱️ {project.duration}</span>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* View All CTA */}
      <div className="port-reveal reveal px-6 md:px-12 max-w-screen-xl mx-auto mt-10 flex justify-center">
        <a
          href="https://wa.me/85256614547"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wider glass-gold transition-all duration-300 hover:scale-105"
          style={{ color: "#D4AF37" }}>
          
          查詢更多項目 · View More Projects
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </section>);

}